import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, X } from "lucide-react";
import { trackConversion } from "@/hooks/use-page-view";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

/**
 * Mobile-only sticky bottom bar that surfaces the next discovery without
 * waiting for the reader to reach end-of-article. Dismissible per article.
 * Hides itself once the user is near the bottom so it doesn't overlap the
 * in-page "Up Next" hero.
 */
export function StickyNextBar({
  nextSlug,
  nextTitle,
  articleSlug,
}: {
  nextSlug: string;
  nextTitle: string;
  articleSlug: string;
}) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = `sa.stickynext.dismiss.${articleSlug}`;
    if (window.sessionStorage.getItem(key)) {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      // Show after 25% scroll, hide in the last 15% so it doesn't cover UpNext.
      const pct = scrolled / docH;
      setVisible(pct > 0.25 && pct < 0.85);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [articleSlug]);

  if (dismissed || !visible) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(`sa.stickynext.dismiss.${articleSlug}`, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 md:hidden">
      <div className="mx-auto max-w-3xl px-3 pb-3">
        <div className="flex items-center gap-2 rounded-2xl border border-primary/40 bg-card/95 p-2 pl-3 shadow-lg backdrop-blur">
          <div className="min-w-0 flex-1">
            <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-primary">
              {ui("Up Next")}
            </div>
            <div className="mt-0.5 truncate font-display text-xs uppercase text-foreground">
              {nextTitle}
            </div>
          </div>
          <Link
            to="/discovery/$slug"
            params={{ slug: nextSlug }}
            onClick={() => trackConversion("next_click", `/discovery/${nextSlug}`)}
            className="plausible-event-name=Sticky+Next+Click inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-primary-foreground"
          >
            {ui("Next")} <ArrowRight className="h-3 w-3" />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label={ui("Dismiss")}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
